import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
export function FlowPreview({ flowJson }) {
    const parseResult = useMemo(() => safeParse(flowJson), [flowJson]);
    const [version, setVersion] = useState(0);
    const [messages, setMessages] = useState([]);
    const [context, setContext] = useState({});
    const [currentStepId, setCurrentStepId] = useState(null);
    const [waitingChoice, setWaitingChoice] = useState(null);
    const [userInput, setUserInput] = useState('');
    const scrollerRef = useRef(null);
    // Reset cuando el JSON cambia o el user pide reset.
    useEffect(() => {
        if (!parseResult.ok)
            return;
        setMessages([]);
        setContext({});
        setCurrentStepId(parseResult.flow.startStepId);
        setWaitingChoice(null);
    }, [parseResult, version]);
    // Auto-run: avanza hasta wait point.
    useEffect(() => {
        if (!parseResult.ok || !currentStepId || waitingChoice)
            return;
        runFromStep(parseResult.flow, currentStepId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStepId, parseResult, waitingChoice]);
    useEffect(() => {
        scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages]);
    function runFromStep(flow, startId) {
        const byId = new Map();
        for (const s of flow.steps)
            byId.set(s.id, s);
        let cursor = startId;
        const local = [];
        const localCtx = { ...context };
        const visited = new Set();
        while (cursor) {
            if (visited.has(cursor)) {
                local.push({ role: 'system', text: `↻ Ciclo detectado en "${cursor}", deteniendo.` });
                break;
            }
            visited.add(cursor);
            const step = byId.get(cursor);
            if (!step) {
                local.push({ role: 'system', text: `❌ Step "${cursor}" no encontrado.` });
                break;
            }
            const action = execStep(step, localCtx);
            if (action.botText)
                local.push({ role: 'bot', text: action.botText });
            if (action.systemText)
                local.push({ role: 'system', text: action.systemText });
            if (action.contextPatch)
                Object.assign(localCtx, action.contextPatch);
            if (action.waitFor) {
                setWaitingChoice(action.waitFor);
                setMessages((prev) => [...prev, ...local]);
                setContext(localCtx);
                setCurrentStepId(action.waitFor.id);
                return;
            }
            cursor = action.next;
        }
        setMessages((prev) => [...prev, ...local, { role: 'system', text: '— Flow completado —' }]);
        setContext(localCtx);
        setCurrentStepId(null);
    }
    function handleChoiceClick(choice) {
        if (!waitingChoice || !parseResult.ok)
            return;
        const next = choice.goto ?? waitingChoice.fallback ?? null;
        setMessages((m) => [...m, { role: 'user', text: choice.label }]);
        if (waitingChoice.assignTo) {
            setContext((c) => ({ ...c, [waitingChoice.assignTo]: choice.id }));
        }
        setWaitingChoice(null);
        setCurrentStepId(next);
    }
    function handleFreeInput() {
        if (!userInput.trim() || !waitingChoice || !parseResult.ok)
            return;
        const text = userInput.trim();
        const match = (waitingChoice.choices ?? []).find((c) => c.id.toLowerCase() === text.toLowerCase() || c.label.toLowerCase() === text.toLowerCase());
        setMessages((m) => [...m, { role: 'user', text }]);
        setUserInput('');
        if (waitingChoice.assignTo && match) {
            setContext((c) => ({ ...c, [waitingChoice.assignTo]: match.id }));
        }
        const next = match ? match.goto ?? waitingChoice.fallback ?? null : waitingChoice.fallback ?? null;
        if (!match)
            setMessages((m) => [...m, { role: 'system', text: '⚠️ Sin match → fallback' }]);
        setWaitingChoice(null);
        setCurrentStepId(next);
    }
    if (!parseResult.ok) {
        return (_jsxs("div", { className: "rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800", children: ["El JSON del flow no es v\u00E1lido: ", parseResult.error] }));
    }
    return (_jsxs("div", { className: "flex h-full flex-col", children: [_jsxs("div", { className: "mb-2 flex items-center justify-between", children: [_jsxs("h3", { className: "text-sm font-semibold", children: ["Simulador \u00B7 ", parseResult.flow.channel ?? 'sin canal'] }), _jsx("button", { type: "button", onClick: () => setVersion((v) => v + 1), className: "rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-100", children: "\u21BB Reiniciar" })] }), _jsxs("div", { ref: scrollerRef, className: "flex-1 overflow-y-auto rounded border border-slate-200 bg-white p-3 space-y-1.5 text-sm", children: [messages.length === 0 && (_jsx("p", { className: "text-xs italic text-slate-400", children: "Esperando primer evento del flow\u2026" })), messages.map((m, i) => (_jsx(Bubble, { msg: m }, i))), waitingChoice && (_jsxs("div", { className: "mt-3 space-y-1", children: [_jsxs("div", { className: "text-[10px] uppercase tracking-wide text-slate-500", children: ["Eleg\u00ED una opci\u00F3n (", waitingChoice.presentation ?? 'buttons', ")"] }), _jsx("div", { className: `flex flex-wrap gap-1 ${waitingChoice.presentation === 'list' ? 'flex-col' : ''}`, children: (waitingChoice.choices ?? []).map((c) => (_jsx("button", { type: "button", onClick: () => handleChoiceClick(c), className: "rounded border border-fuchsia-300 bg-fuchsia-50 px-3 py-1 text-xs text-fuchsia-900 hover:bg-fuchsia-100", children: c.label }, c.id))) })] }))] }), waitingChoice && (_jsxs("div", { className: "mt-2 flex gap-1", children: [_jsx("input", { type: "text", value: userInput, onChange: (e) => setUserInput(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Enter')
                                handleFreeInput();
                        }, placeholder: "O escrib\u00ED el id/label de la opci\u00F3n\u2026", className: "flex-1 rounded border border-slate-300 px-2 py-1 text-xs" }), _jsx("button", { type: "button", onClick: handleFreeInput, className: "rounded bg-brand-500 px-3 py-1 text-xs text-white hover:bg-brand-600", children: "Enviar" })] })), _jsxs("details", { className: "mt-2 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px]", children: [_jsxs("summary", { className: "cursor-pointer text-slate-600", children: ["Contexto (", Object.keys(context).length, ")"] }), _jsx("pre", { className: "mt-1 overflow-x-auto text-[10px] text-slate-700", children: JSON.stringify(context, null, 2) })] })] }));
}
function Bubble({ msg }) {
    if (msg.role === 'system') {
        return _jsx("div", { className: "text-[10px] italic text-slate-400", children: msg.text });
    }
    const isBot = msg.role === 'bot';
    return (_jsx("div", { className: `flex ${isBot ? 'justify-start' : 'justify-end'}`, children: _jsx("div", { className: `max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-1.5 text-xs ${isBot ? 'bg-slate-100 text-slate-800' : 'bg-brand-500 text-white'}`, children: msg.text }) }));
}
function execStep(step, ctx) {
    switch (step.type) {
        case 'text':
            return { botText: interp(step.text ?? '', ctx), next: step.next ?? null };
        case 'set-context':
            return { contextPatch: { ...(step.assign ?? {}) }, next: step.next ?? null };
        case 'condition': {
            const env = { context: ctx };
            for (const b of step.branches ?? []) {
                try {
                    if (evalExpr(b.when, env))
                        return { systemText: `🔀 ${b.when} → ${b.goto}`, next: b.goto };
                }
                catch (err) {
                    return { systemText: `❌ Expr inválida "${b.when}": ${err.message}`, next: step.default ?? null };
                }
            }
            return { systemText: '🔀 else → default', next: step.default ?? null };
        }
        case 'handoff':
            return { systemText: `↪️ Handoff a ${step.target ?? '?'} (${step.reason ?? 'sin motivo'})`, next: step.next ?? null };
        case 'encuesta':
            return { systemText: `📊 Encuesta ${step.surveyId ?? '?'} disparada`, next: step.next ?? null };
        case 'http':
            return { systemText: `🔌 [simulado] ${step.method ?? 'GET'} ${step.url ?? ''}`, next: step.next ?? null };
        case 'llm':
            return { systemText: `🧠 [simulado] LLM ${step.model ?? ''} promptlen=${(step.promptTemplate ?? '').length}`, next: step.next ?? null };
        case 'interactive':
            return { botText: interp(step.prompt ?? '', ctx), next: step.id, waitFor: step };
        case 'send-template':
            // No se renderiza el contenido: la plantilla vive en Meta y acá sólo
            // sabemos su nombre. Mostrar un texto inventado daría una idea falsa de
            // lo que el usuario va a recibir.
            return {
                systemText: `📨 [simulado] plantilla ${step.templateName ?? '?'} (${step.language ?? '?'})`,
                next: step.next ?? null,
            };
        case 'update-contact':
            return {
                systemText: `🗂️ [simulado] guarda en la ficha: ${Object.keys(step.fields ?? {}).join(', ') || '—'}`,
                next: step.next ?? null,
            };
        default:
            return { systemText: `❓ Tipo desconocido: ${step.type}`, next: null };
    }
}
function interp(template, ctx) {
    return template.replace(/\$\{context\.([a-zA-Z0-9_]+)\}/g, (_m, k) => {
        const v = ctx[k];
        return v === undefined || v === null ? '' : String(v);
    });
}
function evalExpr(expr, env) {
    const fn = new Function('context', `with(context){ return (${expr}); }`);
    return Boolean(fn(env.context));
}
function safeParse(json) {
    try {
        const parsed = JSON.parse(json);
        if (!parsed.startStepId || !Array.isArray(parsed.steps)) {
            return { ok: false, error: 'falta startStepId o steps' };
        }
        return { ok: true, flow: parsed };
    }
    catch (e) {
        return { ok: false, error: e.message };
    }
}
//# sourceMappingURL=FlowPreview.js.map